import { createContext, Dispatch, PropsWithChildren, Reducer, useContext, useReducer } from 'react'

import { useFlowSlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'

type Heap = Record<string, any>
type Action = { type: 'add'; fields: Heap } | { type: 'clear' }

const HeapStateContext = createContext<Heap | undefined>(undefined)
HeapStateContext.displayName = 'HeapStateContext'
const HeapDispatchContext = createContext<[Dispatch<Action>, () => void] | undefined>(undefined)
HeapDispatchContext.displayName = 'HeapDispatchContext'

function heapReducer(state: Heap, action: Action): Heap {
  switch (action.type) {
    case 'add':
      return {
        ...state,
        ...action.fields,
      }
    default:
      return state
  }
}

function HeapProvider({ children }: PropsWithChildren): JSX.Element {
  const flowSlug = useFlowSlug()
  const [storedHeap, saveHeap, clearHeap] = useLocalStorage<Heap>(`${flowSlug}/heap`, {})

  const [heap, dispatch] = useReducer<Reducer<Heap, Action>>((state, action) => {
    const newState = heapReducer(state, action)
    saveHeap(newState)
    return newState
  }, storedHeap)

  return (
    <HeapStateContext.Provider value={heap}>
      <HeapDispatchContext.Provider value={[dispatch, clearHeap]}>
        {children}
      </HeapDispatchContext.Provider>
    </HeapStateContext.Provider>
  )
}

const useHeapState = (): Heap => {
  const context = useContext(HeapStateContext)
  if (context === undefined) {
    throw new Error('useHeapState hook must be used within a HeapProvider')
  }

  return context
}

const useHeapDispatch = (): [Dispatch<Action>, () => void] => {
  const context = useContext(HeapDispatchContext)

  if (context === undefined) {
    throw new Error('useHeapState hook must be used within a HeapProvider')
  }

  return context
}

export { HeapProvider, useHeapState, useHeapDispatch, Heap }
